import React, { FunctionComponent as FC, useEffect, useMemo, useRef, useState } from 'react';
import './index.scss';
import { useVideoModal } from '../VideoModal';
import { arch1, arch2, arch3, arch4, figChart } from '../../common/nexusArt';

import conceptFilmUrl from '../../assets/1-media/concept-film.mp4';

const demoVideoId = 'YDaHCgtROck';

const youtubeEmbedUrl = (id: string, params = '') => `https://www.youtube.com/embed/${id}?rel=0${params}`;
const youtubeWatchUrl = (id: string) => `https://www.youtube.com/watch?v=${id}`;

interface DemoPageProps {
}

const DemoPage: FC<DemoPageProps> = () => {
  const { open } = useVideoModal();
  // null = poster; 'auto' = started muted when scrolled into view (browsers block autoplay with sound); 'click' = user hit play
  const [playing, setPlaying] = useState<null | 'auto' | 'click'>(null);
  const walkRef = useRef<HTMLElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const art = useMemo(() => ({ a1: arch1(), a2: arch2(), a3: arch3(), a4: arch4(), walk: figChart() }), []);

  const openFilm = (e?: React.MouseEvent) => {
    e?.preventDefault();
    open({ source: { kind: 'file', src: conceptFilmUrl }, title: 'Figures as interfaces', kicker: 'Concept film · 1:30' });
  };
  const playDemo = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setPlaying('click');
  };

  // autoplay when the walkthrough is half in view, pause when it leaves, resume on return
  const playingRef = useRef(playing);
  playingRef.current = playing;
  useEffect(() => {
    const el = walkRef.current;
    if (!el) return;
    const yt = (func: string) => frameRef.current?.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args: [] }), '*');
    const io = new IntersectionObserver(([en]) => {
      if (!playingRef.current) { if (en.isIntersecting) setPlaying('auto'); return; }
      yt(en.isIntersecting ? 'playVideo' : 'pauseVideo');
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <>
      <section className="banner" id="demo">
        <div className="contain top">
          <h2>Proof-of-Concept: Nexus</h2>
          <div className="r">
            <p>Nexus instantiates the idea in the <a className="inline" href="https://www.science.org/doi/abs/10.1126/science.aao0185" target="_blank" rel="noopener noreferrer">Science of Science</a> domain. Four modules make it work.</p>
            {/* a figure is a structured computational artifact F = {'{'}V, C, D, M{'}'}, with bidirectional mapping between language and interaction, cross-figure coordination, and a versioned exploration history.  */}
          </div>
        </div>
        <div className="contain three four">
          <div className="c"><div className="ill"><svg viewBox="0 0 300 150" id="a1" dangerouslySetInnerHTML={{ __html: art.a1 }} /></div><h3>User Input</h3><p>Natural language and graphical input: type a question, or select, brush and hover on the figures themselves.</p><button type="button" className="btn sm">Read more →</button></div>
          <div className="c"><div className="ill"><svg viewBox="0 0 300 150" id="a2" dangerouslySetInnerHTML={{ __html: art.a2 }} /></div><h3>Hybrid User Interface</h3><p>A language interface (chat and inline chat) and a graphics interface (interactive dashboards) that read and write the same figures.</p><button type="button" className="btn sm">Read more →</button></div>
          <div className="c"><div className="ill"><svg viewBox="0 0 300 150" id="a3" dangerouslySetInnerHTML={{ __html: art.a3 }} /></div><h3>Large-Scale Database</h3><p>SciSci domain data (SciSciNet, SciSciCorpus, ...) plus a chat-history store for users, conversations, artifacts and figure versions.</p><button type="button" className="btn sm">Read more →</button></div>
          <div className="c"><div className="ill"><svg viewBox="0 0 300 150" id="a4" dangerouslySetInnerHTML={{ __html: art.a4 }} /></div><h3>Multi-Agent LLM Engine</h3><p>Planner, Executor and Evaluator over an action space of filtering, transformation, analysis and visualization tools, in SQL, Python and Vega-Lite.</p><button type="button" className="btn sm">Read more →</button></div>
        </div>
      </section>
      <div className="after-banner" />

      <section className="walk" id="walkthrough" ref={walkRef}><div className="contain in">
        {playing ? (
          <div className="player is-playing">
            <div className="pframe">
              <iframe
                ref={frameRef}
                src={youtubeEmbedUrl(demoVideoId, '&autoplay=1&enablejsapi=1&playsinline=1' + (playing === 'auto' ? '&mute=1' : ''))}
                title="Nexus product walkthrough"
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
              />
            </div>
          </div>
        ) : (
          <a className="player" href={youtubeWatchUrl(demoVideoId)} onClick={playDemo} aria-label="Watch the product walkthrough video">
            <div className="pbar"><span className="who"><i></i><b>Nexus</b><small>Yifang Wang</small></span><span className="ic">◁)&nbsp;&nbsp; CC &nbsp;&nbsp;⚙</span></div>
            <div className="pframe"><svg viewBox="0 0 420 290" id="walkfig" dangerouslySetInnerHTML={{ __html: art.walk }} /><span className="play">▶</span></div>
            <div className="pfoot"><span className="time">0:00 / 2:50</span><span className="track"><i></i></span><span className="more">More videos</span></div>
          </a>
        )}
        <div className="wtxt">
          <h2><mark>Product Walkthrough</mark></h2>
          <hr />
          <a className="film" href={conceptFilmUrl} onClick={openFilm}>
            <span className="thumb"><span className="t">A figure that<br /><em>remembers.</em></span><svg viewBox="0 0 80 50"><rect x="2" y="2" width="76" height="46" rx="6" fill="#fff" stroke="var(--grid)" /><polyline points="10,38 22,30 34,20 46,14 56,22 68,34" fill="none" stroke="var(--data)" strokeWidth="2" /></svg></span>
            <span className="ft"><b>Figures as interfaces</b><small>Concept film · 1:30</small></span><span className="fp" aria-hidden="true"><svg viewBox="0 0 12 14"><path d="M1 1.2v11.6a.6.6 0 0 0 .9.5l9.4-5.8a.6.6 0 0 0 0-1L1.9.7a.6.6 0 0 0-.9.5z" /></svg></span>
          </a>
        </div>
      </div></section>
    </>
  )
}

export default DemoPage;
